import config from "../config";
import * as url from "../helpers/url_helper";
import { getResourceStore } from "./crudFactory";
import { applyTransfer } from "./workTransfer";
import { dcCustomerPatchToUserRow } from "./db/accounts";
import { archiveRecord } from "./auditArchive";

/**
 * 三關審核引擎 mock（主計劃 P0-3）。
 *
 * 流程：發動(submit) → 審核(review) → 主管(manager) → 通過(approved)
 * 規則：
 *   - 同一人自動 Pass：若發動者的角色已涵蓋後續關卡，該關自動通過。
 *   - 退回(rejected) 後可重新送審。
 *   - 每筆保留 history（審核歷程）；正式版由後端留存 180 天。
 *
 * 回寫目標資源（ticket 01）：
 *   送審 → 目標只標 approvalStatus: "pending"，其餘欄位不動（退件才救得回來）
 *   通過 → 套用該 action 對應的欄位（APPLY_BY_ACTION）+ approvalStatus: "approved"
 *   退回 → 還原那些欄位的原值 + approvalStatus: "rejected"
 *   目標資源未註冊（或找不到該筆）時靜默略過，審核單本身照常運作。
 *
 * 端點：
 *   POST   /api/approvals              送審 { resource, recordId, action, summary, by }
 *   GET    /api/approvals/:id
 *   GET    /api/approvals?assignee=&status=   待辦整合
 *   POST   /api/approvals/:id/approve  { by, message }
 *   POST   /api/approvals/:id/reject   { by, message }
 */

const STAGES = ["review", "manager"]; // submit 之後要經過的關卡

const latency = () => config.MOCK_LATENCY;
const reply = (status, body) =>
  new Promise((resolve) => setTimeout(() => resolve([status, body]), latency()));

let approvals = [];
let seq = 1;

const now = () => new Date().toISOString();

/**
 * 單據編號(260804 稿:日期制 RQ-YYYYMMDD-NNN)。與前端 `formatDocNo()` 同一個
 * 規則 —— 這裡不 import 元件層,但兩邊的格式必須一致,否則畫面上的編號跟
 * 審核中心對不起來(issue 02)。
 */
const docNoOf = (id, createdAt) => {
  const d = createdAt ? new Date(createdAt) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `RQ-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${String(id % 1000).padStart(3, "0")}`;
};

// —— 種子:讓審核中心/審核詳情一進來就有可檢視的單據(對齊 Figma 範例) ——
// 註:dev session 以 Overseer 登入,新送審會同一人自動通過,故需種子提供 pending 單。
function seedApprovals() {
  return [
    {
      id: 9001, resource: "role", recordId: 12, action: "create",
      summary: "稽核人員（自訂角色）",
      initiator: "陳雅婷（資訊處）", initiatorRole: "Viewer",
      reviewer: "林承翰（資訊處）", manager: "王志明（總經理室）",
      stages: ["review", "manager"], stage: "review", pendingStage: "review",
      createdAt: "2026-06-05T16:03:00+08:00",
      history: [
        { stage: "submit", action: "submit", by: "陳雅婷（資訊處）", message: "送出申請:新增自訂角色「稽核人員」。", at: "2026-06-05T16:03:00+08:00" },
      ],
    },
    {
      id: 9002, resource: "user", recordId: 34, action: "create",
      summary: "使用者管理－新增 李宜潔",
      initiator: "張家豪（維運部）", initiatorRole: "Viewer",
      reviewer: "林承翰（資訊處）", manager: "王志明（總經理室）",
      stages: ["review", "manager"], stage: "manager", pendingStage: "manager",
      createdAt: "2026-06-05T15:40:00+08:00",
      history: [
        { stage: "submit", action: "submit", by: "張家豪（維運部）", message: "送出申請:新增使用者「李宜潔」。", at: "2026-06-05T15:40:00+08:00" },
        { stage: "review", action: "approve", by: "林承翰（資訊處）", message: "資料正確，同意。", at: "2026-06-05T16:10:00+08:00" },
      ],
    },
    {
      id: 9003, resource: "room", recordId: 3, action: "create",
      summary: "機房位置－新增 桃園華亞機房",
      initiator: "王志明（總經理室）", initiatorRole: "Overseer",
      reviewer: "王志明（總經理室）", manager: null,
      stages: ["review"], stage: "approved", pendingStage: null,
      createdAt: "2026-06-04T10:00:00+08:00",
      history: [
        { stage: "submit", action: "submit", by: "王志明（總經理室）", message: "送出申請:新增機房「桃園華亞機房」。", at: "2026-06-04T10:00:00+08:00" },
        { stage: "review", action: "approve", by: "王志明（總經理室）", message: "同一人自動 Pass", auto: true, at: "2026-06-04T10:00:00+08:00" },
      ],
    },
    {
      id: 9004, resource: "role", recordId: 3, action: "update",
      summary: "角色管理－異動 Viewer 權限調整",
      initiator: "李宜潔（資訊處）", initiatorRole: "Viewer",
      reviewer: "林承翰（資訊處）", manager: null,
      stages: ["review"], stage: "rejected", pendingStage: null,
      createdAt: "2026-06-03T09:12:00+08:00",
      history: [
        { stage: "submit", action: "submit", by: "李宜潔（資訊處）", message: "送出申請:調整 Viewer 角色權限。", at: "2026-06-03T09:12:00+08:00" },
        { stage: "review", action: "reject", by: "林承翰（資訊處）", message: "權限配置與既有角色重複，請調整後重新送審。", at: "2026-06-03T10:20:00+08:00" },
      ],
    },
  ];
}

// 依角色決定可自動 Pass 的關卡（admin 兩關全包 → 同一人自動通過）
// 同一人自動 Pass:發動者角色可涵蓋的關卡自動通過(客戶確認單 Q1/Q2)
// Overseer(老闆)= 第三關,涵蓋 review+manager;Gatekeeper(主管)= 第二關,涵蓋 review。
const stagesCoveredBy = (role) => {
  if (role === "Overseer") return ["review", "manager"];
  if (role === "Gatekeeper") return ["review"];
  return [];
};

// —— 回寫目標資源 ——
// 「哪個 action 核准後要把目標改成什麼」是後端規則,放在這裡而不是頁面:
// 前端只送出「我要做這件事」,鎖定與否由審核結果決定,真後端到位時前端不用改。
/**
 * 後端政策決定的欄位:某個 action 一律套用這些值(與使用者這次改了什麼無關)。
 * 「使用者這次改了什麼」是資料不是政策,走審核單的 payload(ticket 30)。
 */
const APPLY_BY_ACTION = {
  close: { status: "已結案", locked: true },
  // 通知規則的暫停/啟用(9月交付 ticket 09):同上,核准後才切 active
  "rule-pause": { active: false },
  "rule-resume": { active: true },
};

/**
 * 只對某個資源成立的政策(key = `資源:動作`),優先於 APPLY_BY_ACTION。
 *
 * 「新增核准」對大多數資源就是生效,但硬體是兩段式:核准只代表位置留給你了,
 * 機器還沒進機房 —— 所以進入「已核准待上架」,不是直接上架(兩段式新增 ticket 02)。
 * 沒有這層的話,lifecycle 會被寫到 user/role 這些根本沒這個概念的資源上。
 */
const APPLY_BY_RESOURCE_ACTION = {
  // 通知規則(9月交付 ticket 10):精靈建立時 active:false(未生效),核准才啟用
  "notify-rule:create": { active: true },
  "hardware:create": { lifecycle: "reserved" },
  // 回報上架完成(ticket 04):機器實體裝好了,核准後 U 位由保留轉為正式佔用。
  // 刻意不叫 close —— 既有的 close 是汰除,兩者的命名衝突尚未定案(見 spec)。
  "hardware:install": { lifecycle: "commissioned" },
  // 撤回(ticket 06):核准後單據作廢、位置放掉 —— 回到草稿即等於不佔位也不保留
  "hardware:withdraw": { lifecycle: "draft" },
};

/** 同一筆資料同時只准一張進行中的單據的資源(理由見送審處的註解) */
const ONE_OPEN_APPROVAL_RESOURCES = new Set(["hardware"]);

const storeFor = (a) => (a.resource ? getResourceStore(a.resource) : null);

/**
 * 核准後要寫回目標的欄位 = 政策欄位 + 這次異動的內容。
 * 轉派不走這裡(它搬的是一整批關係,見 workTransfer.js)。
 */
const applyFieldsFor = (a) => {
  const policy = APPLY_BY_RESOURCE_ACTION[`${a.resource}:${a.action}`] || APPLY_BY_ACTION[a.action] || null;
  const changes = a.payload && a.action !== "transfer" ? a.payload : null;
  if (!policy && !changes) return null;
  return { ...(policy || {}), ...(changes || {}) };
};

function patchTarget(a, fields) {
  const store = storeFor(a);
  if (!store) return; // 未註冊的資源：靜默略過
  store.patch(a.recordId, fields);
}

/**
 * 送審當下先記下 apply 會蓋掉的欄位原值。
 * 兩個用途:退回要還原得了,以及審核詳情要看得出「從什麼改成什麼」。
 */
function snapshotTarget(a) {
  const store = storeFor(a);
  const fields = applyFieldsFor(a);
  if (!store || !fields) return null;
  const row = store.find(a.recordId);
  if (!row) return null;
  return Object.keys(fields).reduce((acc, k) => ({ ...acc, [k]: row[k] }), {});
}

// 依審核單目前狀態把結果寫回目標
function syncTarget(a) {
  const status = currentStatus(a);
  // 轉派搬的是一整批負責關係,不是單一筆資料,所以走自己的副作用(ticket 33)
  if (a.action === "transfer") return applyTransfer(a, status);

  // 刪除:核准前只鎖住(資料還在,退件救得回來),核准後才移入稽核保留區(ticket 08)
  if (a.action === "delete") {
    if (status === "approved") {
      archiveRecord({ resource: a.resource, recordId: a.recordId, approvalId: a.id, by: a.initiator });
    } else if (status === "rejected") {
      patchTarget(a, { approvalStatus: "rejected", locked: false });
    } else {
      patchTarget(a, { approvalStatus: "pending", locked: true });
    }
    return;
  }
  if (status === "approved") {
    patchTarget(a, { ...(applyFieldsFor(a) || {}), approvalStatus: "approved" });
    appendIpHistory(a);
  } else if (status === "rejected") patchTarget(a, { ...(a.snapshot || {}), approvalStatus: "rejected" });
  // 撤回＝單據作廢:回到送審前的樣子,而且不再凍結(才能重新發動一張新的)
  else if (status === "withdrawn") patchTarget(a, { ...(a.snapshot || {}), approvalStatus: "withdrawn" });
  // pendingAction 讓列表分得出「審核中(新增)」與「異動中」(票 02 IP 狀態欄);
  // 核准/退回後 approvalStatus 已非 pending,殘留值不影響顯示
  else patchTarget(a, { approvalStatus: "pending", docNo: docNoOf(a.id, a.createdAt), pendingAction: a.action });
}

/**
 * IP 的異動歷程由審核結果回寫(票 02):新增/異動核准即記一筆。
 * SNMP 探測的「系統自動寫入(不需簽核)」不走審核,由種子/探測端自己記。
 */
function appendIpHistory(a) {
  if (a.resource !== "ip" || !["create", "update"].includes(a.action)) return;
  const store = storeFor(a);
  const row = store && store.find(a.recordId);
  if (!row) return;
  const kind = a.action === "create" ? "新增" : "異動";
  const entry = {
    at: new Date().toISOString().slice(0, 16).replace("T", " "),
    by: a.initiator,
    kind,
    text: a.summary || `${kind} IP ${row.ip || ""}`,
    docNo: a.docNo || docNoOf(a.id, a.createdAt),
    // 值域照 swagger DcIpAddressChangeLogDto.status_text(半形括號)
    result: "已核准(結案)",
  };
  store.patch(a.recordId, { history: [entry, ...(row.history || [])] });
}

function currentStatus(a) {
  if (a.stage === "approved") return "approved";
  if (a.stage === "rejected") return "rejected";
  if (a.stage === "withdrawn") return "withdrawn";
  return "pending";
}

// 該關卡指定的簽核人(選擇審核對象 dialog 指定;未指定則為 undefined)
const assigneeFor = (a, stage) =>
  stage === "review" ? a.reviewer : stage === "manager" ? a.manager : null;

// 關卡是否自動 Pass:發動者角色涵蓋該關,或指定簽核人＝發動者(同一人)
function stageAutoPass(a, stage) {
  if (stagesCoveredBy(a.initiatorRole).includes(stage)) return true;
  const assignee = assigneeFor(a, stage);
  return !!assignee && assignee === a.initiator;
}

function autoAdvance(a) {
  const stages = a.stages || STAGES;
  // 從目前 pendingStage 起，凡可自動通過(角色涵蓋或同一人)的關卡自動 Pass
  let idx = stages.indexOf(a.pendingStage);
  while (idx < stages.length && stageAutoPass(a, stages[idx])) {
    a.history.push({
      stage: stages[idx],
      action: "approve",
      by: assigneeFor(a, stages[idx]) || a.initiator,
      message: "同一人自動 Pass",
      auto: true,
      at: now(),
    });
    idx += 1;
  }
  if (idx >= stages.length) {
    a.stage = "approved";
    a.pendingStage = null;
  } else {
    a.pendingStage = stages[idx];
    a.stage = stages[idx];
  }
}

function idFromUrl(u, suffix = "") {
  const path = u.split("?")[0].replace(suffix, "");
  return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1));
}

/** 看板 GetTodo／GetTodoItems 讀同一份狀態機，不要再抄一份種子 */
export function listApprovals() {
  return approvals.slice();
}

export default function registerApprovals(mock, { seed = false } = {}) {
  const base = url.DCK.APPROVALS;

  // 每次重建 mock 就把狀態機清乾淨 —— 這是模組層級的陣列,不清的話上一個測試
  // 留下的 pending 單會讓下一個測試撞到「已有進行中的單據」。
  approvals.length = 0;
  seq = 1;
  // 只有 app(setupMock)要種子;單元測試不種子以維持乾淨狀態機
  if (seed) approvals.push(...seedApprovals());

  // 送審
  mock.onPost(base).reply((cfg) => {
    const p = cfg.data ? JSON.parse(cfg.data) : {};
    // 選擇審核對象:指定第二關審核者(reviewer)、選填第三關主管核決(manager)。
    // 有指定 reviewer(新流程)→ 關卡依指定;未指定 manager → 本單至第二關結案(2 關)。
    // 未帶 reviewer(舊流程/測試)→ 沿用預設三關(review+manager),靠角色自動 Pass。
    const hasApprovers = p.reviewer !== undefined;
    const stages = hasApprovers ? (p.manager ? ["review", "manager"] : ["review"]) : STAGES;
    const record = {
      id: seq++,
      resource: p.resource || "",
      recordId: p.recordId,
      action: p.action || "create", // create/update/delete/import/transfer
      // 這次異動的內容。目前只有轉派用得到(要搬哪幾筆、搬給誰);
      // ticket 30 之後其他異動也會靠它做到「核准才寫入」。
      // user 資源的 payload 走後端契約(snake_case,ticket 04);套用對象是
      // camelCase user row,入口先轉一次,後續 snapshot/套用/顯示都一致。
      payload:
        p.resource === "user" && p.payload && p.action !== "transfer"
          ? dcCustomerPatchToUserRow(p.payload)
          : p.payload || null,
      summary: p.summary || "",
      initiator: p.by || "current-user",
      initiatorRole: p.role || "user",
      reviewer: p.reviewer,
      manager: p.manager || null,
      stages,
      stage: stages[0],
      pendingStage: stages[0],
      createdAt: now(),
      history: [
        { stage: "submit", action: "submit", by: p.by || "current-user", message: p.summary || "送出審核", at: now() },
      ],
    };
    // 刪除:關聯還沒解除就根本不讓送,而且要講得出卡在哪、怎麼解(需求書 §2.2.3)。
    // 擋在這裡而不是等到真的刪 —— 半成品的單子比擋下來更難收拾。
    if (record.action === "delete") {
      const store = storeFor(record);
      const blocked = store && store.blockedReason ? store.blockedReason(record.recordId) : null;
      if (blocked) return reply(409, { message: blocked });
    }

    // 撤回單不重新選人(ticket 06):位置是因為那張單才被鎖起來的,放掉它的人
    // 應該是當初同意鎖的人。重新選人等於讓發動者挑一個好講話的來放掉別人在等的位置。
    if (record.action === "withdraw") {
      const origin = approvals
        .filter((x) => x.resource === record.resource
          && String(x.recordId) === String(record.recordId)
          && currentStatus(x) === "approved")
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      if (origin) {
        record.reviewer = origin.reviewer;
        record.manager = origin.manager;
        record.stages = origin.stages;
        record.stage = record.stages[0];
        record.pendingStage = record.stages[0];
        record.withdrawOf = origin.id;
      }
    }

    // 同一筆硬體同時只能有一張進行中的單據(兩段式新增 ticket 02)。
    //
    // 只擋硬體:兩段式是一連串有先後的單(新增 → 回報上架 → …),
    // 兩張同時跑會把 lifecycle 推到互相矛盾的狀態。其他資源目前允許並行的
    // 欄位級異動(見 approvalEdgeCases 那兩支測試),要不要一起收斂是另一個決定。
    if (ONE_OPEN_APPROVAL_RESOURCES.has(record.resource)) {
      const open = approvals.find(
        (x) => x.resource === record.resource
          && String(x.recordId) === String(record.recordId)
          && currentStatus(x) === "pending"
      );
      if (open) {
        return reply(409, { message: `這筆資料已有一張進行中的審核單(${docNoOf(open.id, open.createdAt)}),請先完成或撤回。` });
      }
    }

    record.snapshot = snapshotTarget(record); // 先存原值,退回才還原得了
    autoAdvance(record); // 同一人自動 Pass
    approvals.push(record);
    syncTarget(record);
    return reply(200, { ...record, status: currentStatus(record) });
  });

  // 待辦列表 / 單筆
  mock.onGet(base).reply((cfg) => {
    const params = cfg.params || {};
    let rows = approvals.slice();
    if (params.status) rows = rows.filter((a) => currentStatus(a) === params.status);
    if (params.resource) rows = rows.filter((a) => a.resource === params.resource);
    // 詳情頁要問「這筆資料有哪些審核單」→ resource + recordId
    if (params.recordId != null) rows = rows.filter((a) => String(a.recordId) === String(params.recordId));
    const data = rows.map((a) => ({ ...a, status: currentStatus(a) }));
    return reply(200, { data, total: data.length });
  });

  mock.onGet(new RegExp(`^${base}/[^/]+$`)).reply((cfg) => {
    const id = idFromUrl(cfg.url);
    const a = approvals.find((x) => String(x.id) === id);
    return a ? reply(200, { ...a, status: currentStatus(a) }) : reply(404, { message: "查無審核單" });
  });

  /**
   * 撤回（ticket 06）—— 只給**還在審核中**的單:那個位置本來還沒鎖給你,
   * 收回來不用驚動別人,所以直接作廢,不產生新的審核單。
   *
   * 已核准的階段要放掉位置,走的是另一條路:發一張 action="withdraw" 的新單,
   * 沿用原單的關卡（見上方送審處）。
   */
  mock.onPost(new RegExp(`^${base}/[^/]+/withdraw$`)).reply((cfg) => {
    const id = idFromUrl(cfg.url, "/withdraw");
    const p = cfg.data ? JSON.parse(cfg.data) : {};
    const a = approvals.find((x) => String(x.id) === id);
    if (!a) return reply(404, { message: "查無審核單" });
    if (currentStatus(a) !== "pending") {
      return reply(409, { message: "此審核單已結案,無法撤回。" });
    }
    // 只有發動者本人 —— 不是任何有權限的人都能把別人的單收掉。
    // 用 409 不用 403:依 helpers/errorMessages 的約定,403 會被換成制式文案,
    // 而這句「去找發動者」是使用者需要知道的下一步(與刪除阻擋同一個理由)。
    if (p.by && a.initiator && p.by !== a.initiator) {
      return reply(409, { message: "只有發動者本人可以撤回這張單據。" });
    }
    a.history.push({
      stage: a.pendingStage, action: "withdraw",
      by: p.by || a.initiator, message: p.message || "發動者撤回", at: now(),
    });
    a.stage = "withdrawn";
    a.pendingStage = null;
    syncTarget(a);
    return reply(200, { ...a, status: currentStatus(a) });
  });

  // 核准（推進一關）
  mock.onPost(new RegExp(`^${base}/[^/]+/approve$`)).reply((cfg) => {
    const id = idFromUrl(cfg.url, "/approve");
    const p = cfg.data ? JSON.parse(cfg.data) : {};
    const a = approvals.find((x) => String(x.id) === id);
    if (!a) return reply(404, { message: "查無審核單" });
    if (a.stage === "approved" || a.stage === "rejected") {
      return reply(409, { message: "此審核單已結案" });
    }
    a.history.push({ stage: a.pendingStage, action: "approve", by: p.by || "approver", message: p.message || "核准", at: now() });
    const stages = a.stages || STAGES;
    const idx = stages.indexOf(a.pendingStage);
    if (idx >= stages.length - 1) {
      a.stage = "approved";
      a.pendingStage = null;
    } else {
      // 推進到下一關;若下一關可自動 Pass(同一人)則連帶通過
      a.pendingStage = stages[idx + 1];
      a.stage = stages[idx + 1];
      autoAdvance(a);
    }
    syncTarget(a);
    return reply(200, { ...a, status: currentStatus(a) });
  });

  // 退回
  mock.onPost(new RegExp(`^${base}/[^/]+/reject$`)).reply((cfg) => {
    const id = idFromUrl(cfg.url, "/reject");
    const p = cfg.data ? JSON.parse(cfg.data) : {};
    const a = approvals.find((x) => String(x.id) === id);
    if (!a) return reply(404, { message: "查無審核單" });
    a.history.push({ stage: a.pendingStage, action: "reject", by: p.by || "approver", message: p.message || "退回", at: now() });
    a.stage = "rejected";
    a.pendingStage = null;
    syncTarget(a);
    return reply(200, { ...a, status: currentStatus(a) });
  });
}
